import makeStyles from '@material-ui/core/styles/makeStyles';
import { ICertificate } from 'interface/certificate';
import { useEffect, useState } from 'react';
import * as CertificateService from 'services/certificate';
import noCertificate from 'assets/img/no-certificate.png';
import Card from './Card';
import { Typography } from '@material-ui/core';
import CenteredPage from 'components/Shared/CenteredPage';
import Skeleton from '@material-ui/lab/Skeleton';

const useStyles = makeStyles((theme) => ({
  searchList: {
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'center',
    flexFlow: 'row wrap',
    width: '100%',
    '& > div': {
      margin: theme.spacing(1),
    },
  },
  emptyState: {
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
    height: '76vh',
    alignItems: 'center',
    alignContent: 'center',
    flexDirection: 'column',
    '& > img': {
      width: 536,
    },
    '& > .title': {
      fontSize: 20,
      fontWeight: 'bold',
      color: theme.palette.common.white,
      marginTop: 32,
    },
    '& > .sub-title': {
      fontSize: 16,
      marginTop: 16,
      color: theme.palette.common.white,
    },
  },
  skeleton: {
    width: '18.7vw',
    '@media(max-width: 1582px)': {
      height: '12.7vw',
    },
    '@media(max-width: 1200px)': {
      height: '12.3vw',
    },
    '@media(min-width: 1600px)': {
      height: '12.7vw',
    },
    '@media(min-width: 2560px)': {
      height: '8.2vw',
    },
    '@media(max-width: 1024px)': {
      minWidth: 181,
      width: 181,
    },
    position: 'relative',
    background: theme.palette.dark[600],
    height: '12.8vw',
    overflow: 'hidden',
    borderRadius: 8,
  },
  fakeCard: {
    visibility: 'hidden',
    minHeight: 0,
    maxHeight: 0,
  },
}));

interface IProps {
  slice?: number;
}

const CertificateList = ({ slice }: IProps) => {
  const [certificates, setCertificates] = useState<ICertificate[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const classes = useStyles();

  useEffect(() => {
    CertificateService.fetchCertificates()
      .then(({ data }) => data['data'])
      .then((certificates) => {
        if (slice) return certificates.slice(0, slice);
        return certificates;
      })
      .then(setCertificates)
      .finally(() => setIsLoading(false));
  }, [slice]);

  if (isLoading) {
    return (
      <CenteredPage marginTop={12}>
        <div className={classes.searchList}>
          <Skeleton className={classes.skeleton} variant='rect' component='div' />
          <Skeleton className={classes.skeleton} variant='rect' component='div' />
          <Skeleton className={classes.skeleton} variant='rect' component='div' />
        </div>
      </CenteredPage>
    );
  }

  if (!certificates || certificates?.length === 0) {
    return (
      <CenteredPage marginTop={12}>
        <div className={classes.emptyState}>
          <img alt='Imagem ilustrativa de sem certificados' src={noCertificate} />
          <Typography className='title'>Todos seus certificados aparecerão aqui...</Typography>
          <Typography className='sub-title'>Aqui você poderá consultar e baixar todos seus certificados.</Typography>
        </div>
      </CenteredPage>
    );
  }

  return (
    <CenteredPage marginTop={12}>
      <div className={classes.searchList}>
        {certificates.map((certificate: ICertificate, index: number) => (
          <Card
            key={`card-${index}`}
            title={certificate.title}
            id={certificate.id}
            image={certificate.course_image}
            url={certificate.url}
            courseHash={certificate.hash}
          />
        ))}
        <Skeleton className={[classes.skeleton, classes.fakeCard].join(' ')} variant='rect' component='div' />
        <Skeleton className={[classes.skeleton, classes.fakeCard].join(' ')} variant='rect' component='div' />
        <Skeleton className={[classes.skeleton, classes.fakeCard].join(' ')} variant='rect' component='div' />
        <Skeleton className={[classes.skeleton, classes.fakeCard].join(' ')} variant='rect' component='div' />
        <Skeleton className={[classes.skeleton, classes.fakeCard].join(' ')} variant='rect' component='div' />
      </div>
    </CenteredPage>
  );
};

export default CertificateList;
